import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import Axios from "../../api-utility/axiosInstance";

// 1. FETCH OVERVIEW REPORT
export const fetchReportOverview = createAsyncThunk(
  "reports/fetchOverview",
  async (params = {}, { rejectWithValue, signal }) => {
    try {
      const res = await Axios.get("/tenant/reports/overview", { params, signal });
      if (res.data?.success === false) {
        return rejectWithValue(res.data.message);
      }
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || "Failed to fetch overview report");
    }
  }
);

// 2. FETCH LEAD REPORTS
export const fetchLeadReports = createAsyncThunk(
  "reports/fetchLeads",
  async (params = {}, { rejectWithValue, signal }) => {
    try {
      const res = await Axios.get("/tenant/reports/leads", { params, signal });
      if (res.data?.success === false) {
        return rejectWithValue(res.data.message);
      }
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || "Failed to fetch lead reports");
    }
  }
);

// 3. FETCH FOLLOW-UP REPORTS
export const fetchFollowupReports = createAsyncThunk(
  "reports/fetchFollowups",
  async (params = {}, { rejectWithValue, signal }) => {
    try {
      const res = await Axios.get("/tenant/reports/followups", { params, signal });
      if (res.data?.success === false) {
        return rejectWithValue(res.data.message);
      }
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || "Failed to fetch followup reports");
    }
  }
);

// 4. FETCH QUOTATION REPORTS
export const fetchQuotationReports = createAsyncThunk(
  "reports/fetchQuotations",
  async (params = {}, { rejectWithValue, signal }) => {
    try {
      const res = await Axios.get("/tenant/reports/quotations", { params, signal });
      if (res.data?.success === false) {
        return rejectWithValue(res.data.message);
      }
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || "Failed to fetch quotation reports");
    }
  }
);

const initialState = {
  overview: {
    kpis: {
      total_leads: 0,
      won_leads: 0,
      lead_conversion_rate: 0,
      total_followups: 0,
      completed_followups: 0,
      overdue_followups: 0,
      followup_completion_rate: 0,
      total_quotations: 0,
      won_quotations: 0,
      quotation_win_rate: 0,
      total_quoted_amount: 0,
      won_revenue: 0,
      avg_deal_size: 0,
    },
    funnel: [],
    monthly_trend: [],
  },
  leads: {
    summary: {
      total_leads: 0,
      won_leads: 0,
      total_budget: 0,
      avg_budget_per_lead: 0,
    },
    stage_funnel: [],
    source_breakdown: [],
    rep_performance: [],
  },
  followups: {
    summary: {
      total_followups: 0,
      completed: 0,
      overdue: 0,
      due_today: 0,
      rescheduled: 0,
      completion_rate: 0,
      overdue_rate: 0,
    },
    ageing_buckets: [],
    type_breakdown: [],
    rep_matrix: [],
  },
  quotations: {
    summary: {
      total_quotations: 0,
      won_quotations: 0,
      win_rate: 0,
      total_pipeline_value: 0,
      won_revenue: 0,
      avg_deal_size: 0,
      total_discount_given: 0,
      avg_discount_percentage: 0,
      revisions_count: 0,
    },
    status_distribution: [],
    rep_leaderboard: [],
  },
  overviewLoading: false,
  leadsLoading: false,
  followupsLoading: false,
  quotationsLoading: false,
  error: null,
};

const reportSlice = createSlice({
  name: "reports",
  initialState,
  reducers: {
    clearReportErrors: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Aborted requests were superseded by a newer tab/filter fetch, so they are ignored
    builder
      // Overview
      .addCase(fetchReportOverview.pending, (state) => {
        state.overviewLoading = true;
        state.error = null;
      })
      .addCase(fetchReportOverview.fulfilled, (state, action) => {
        state.overviewLoading = false;
        state.overview = action.payload || initialState.overview;
      })
      .addCase(fetchReportOverview.rejected, (state, action) => {
        if (action.meta.aborted) return;
        state.overviewLoading = false;
        state.error = action.payload || "Failed to fetch overview report";
      })

      // Leads
      .addCase(fetchLeadReports.pending, (state) => {
        state.leadsLoading = true;
        state.error = null;
      })
      .addCase(fetchLeadReports.fulfilled, (state, action) => {
        state.leadsLoading = false;
        state.leads = action.payload || initialState.leads;
      })
      .addCase(fetchLeadReports.rejected, (state, action) => {
        if (action.meta.aborted) return;
        state.leadsLoading = false;
        state.error = action.payload || "Failed to fetch lead reports";
      })

      // Followups
      .addCase(fetchFollowupReports.pending, (state) => {
        state.followupsLoading = true;
        state.error = null;
      })
      .addCase(fetchFollowupReports.fulfilled, (state, action) => {
        state.followupsLoading = false;
        state.followups = action.payload || initialState.followups;
      })
      .addCase(fetchFollowupReports.rejected, (state, action) => {
        if (action.meta.aborted) return;
        state.followupsLoading = false;
        state.error = action.payload || "Failed to fetch followup reports";
      })

      // Quotations
      .addCase(fetchQuotationReports.pending, (state) => {
        state.quotationsLoading = true;
        state.error = null;
      })
      .addCase(fetchQuotationReports.fulfilled, (state, action) => {
        state.quotationsLoading = false;
        state.quotations = action.payload || initialState.quotations;
      })
      .addCase(fetchQuotationReports.rejected, (state, action) => {
        if (action.meta.aborted) return;
        state.quotationsLoading = false;
        state.error = action.payload || "Failed to fetch quotation reports";
      });
  },
});

export const { clearReportErrors } = reportSlice.actions;
export default reportSlice.reducer;
